import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "@/lib/http";

/**
 * Sort categories by numeric `order` ascending; nulls last.
 */
function sortByOrder(cats) {
  return [...cats].sort((a, b) => {
    const ao = a.order === null || a.order === undefined ? Infinity : a.order;
    const bo = b.order === null || b.order === undefined ? Infinity : b.order;
    if (ao === bo) return String(a.name || "").localeCompare(String(b.name || ""));
    return ao - bo;
  });
}

/**
 * Sequentially loads every category, then its products, one at a time.
 * Dispatches progress actions after each category resolves so the UI can
 * stream chips + products as they arrive. Fires only once per session —
 * subsequent visits to the Products page reuse the cached state.
 */
export const loadProductsSequentially = createAsyncThunk(
  "products/loadSequentially",
  async (_arg, { dispatch, getState, rejectWithValue }) => {
    const state = getState().products;
    if (state.status === "loading" || state.status === "succeeded") {
      // Already cached or currently streaming — bail out.
      return;
    }
    dispatch(streamStarted());
    let cats = [];
    try {
      const res = await api.get("/categories");
      cats = sortByOrder(res.data || []);
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to load categories");
    }

    for (const cat of cats) {
      try {
        const pRes = await api.get("/products", { params: { category: cat.slug } });
        dispatch(categoryLoaded({ category: cat, products: pRes.data || [] }));
      } catch (err) {
        // Surface the chip even if the product fetch failed so the user can move on.
        dispatch(categoryLoaded({ category: cat, products: [] }));
      }
    }
    return;
  }
);

const initialState = {
  loadedCategories: [],
  products: [],
  initialReady: false,   // first category + its products have arrived
  streaming: false,      // still fetching remaining categories
  status: "idle",        // idle | loading | succeeded | failed
  error: null,
};

const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    streamStarted(state) {
      state.status = "loading";
      state.streaming = true;
      state.error = null;
    },
    categoryLoaded(state, action) {
      const { category, products } = action.payload;
      // Avoid duplicate chips if the thunk is somehow re-fired
      if (!state.loadedCategories.some((c) => c.id === category.id)) {
        state.loadedCategories.push(category);
      }
      state.products.push(...products);
      state.initialReady = true;
    },
    // Manual cache invalidation — call this if the admin adds/edits a product
    invalidateProducts() {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadProductsSequentially.fulfilled, (state) => {
        state.status = "succeeded";
        state.streaming = false;
        state.initialReady = true;
      })
      .addCase(loadProductsSequentially.rejected, (state, action) => {
        state.status = "failed";
        state.streaming = false;
        state.initialReady = true;
        state.error = action.payload || action.error?.message || "Unknown error";
      });
  },
});

export const { streamStarted, categoryLoaded, invalidateProducts } = productsSlice.actions;
export default productsSlice.reducer;
